import { useEffect, useMemo } from "react";
import { usePermissionsStore } from "@/stores/permissions-store";
import {
  EntityPermissions,
  getMyPyloPermissions,
  getPyloAcos,
  PyloAco,
} from "@/services/acl.service";
import { FieldPermissionRow } from "@/hooks/schema/use-my-permissions";
import { parseAllowedPaths } from "@/components/acl/permission-allowed-paths";
import { useMyUser } from "@/hooks/auth/use-my-user";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { useCurrentUserId } from "@/contexts/customer-id-context";
import { CONSTANTS } from "@/lib/constants";

// We should only use this hook at one point in the application.
// We fetch, refetch and parse the permissions and write them to a zustand store.
// the rest of the application can then consume from the zustand store
export const useInitializePermissions = () => {
  const {
    setEntityPermissionsMap,
    setEntityFieldPermissionsMap,
    setIsLoading,
    setHasRootPrivileges,
    setHasLoaded,
  } = usePermissionsStore();

  // The user id comes straight from the JWT (SSR-seeded into context), with a
  // fallback to the `me`-resolved id for any edge-case first render without a
  // token. This lets the permissions query fire immediately on mount.
  const userIdFromToken = useCurrentUserId();
  const { myUser } = useMyUser();
  const userId = userIdFromToken ?? myUser?.current_user?.data?.id ?? null;

  // Permissions are fetched in a single query, filtered directly by the user
  // relationship on the ARO. This replaces the old aros -> permissionsForAroIds
  // waterfall: it depends only on the JWT user id (so it fires in parallel with
  // everything else instead of waiting for the AROs round-trip), the DB dedupes
  // each permission row, and it carries no ARO payload.
  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isFetched: isFetchedPermissions,
  } = useAuthenticatedQuery({
    queryKey: ["permissions", "myPermissions", userId],
    queryFn: () => getMyPyloPermissions({ userId: userId ?? "" }),
    enabled: !!userId,
  });

  const {
    data: acos,
    isLoading: isLoadingAcos,
    isFetched: isFetchedAcos,
  } = useAuthenticatedQuery({
    queryKey: ["permissions", "acos"],
    queryFn: getPyloAcos,
    // getPyloAcos takes no user-specific args, so it doesn't need to wait for
    // `me`. Firing immediately takes it out of the me→acos waterfall.
    // (useAuthenticatedQuery still gates on auth / public-page.)
  });

  const isLoadingCombined = isLoadingPermissions || isLoadingAcos;

  const entityPermissionsMap = useMemo(() => {
    if (!permissions || !acos) {
      return {
        permissionsForEntityIds: new Map<string, EntityPermissions>(),
        permissionsByAcoId: new Map<string, EntityPermissions>(),
        fieldPermissionsForEntityIds: new Map<string, FieldPermissionRow[]>(),
      };
    }

    const acosMap = new Map<string, PyloAco>();
    acos.data?.forEach((aco) => {
      acosMap.set(aco.id, aco);
    });

    // Create a map of permissions by ACO ID for quick lookup
    const permissionsByAcoId = new Map<string, EntityPermissions>();

    // Field-level rows are kept un-merged (one entry per permission row) so each row's
    // `field_paths` stays paired with the operations it grants — OR-ing the booleans together (as
    // below) would lose which paths apply to which operation.
    const fieldRowsByAcoId = new Map<string, FieldPermissionRow[]>();

    // Merge all permissions that exist for one ACO
    permissions.data?.forEach((permission) => {
      const acoId = permission.pylo_aco.data.id;
      const existingPermission = permissionsByAcoId.get(acoId);
      permissionsByAcoId.set(acoId, {
        has_create: existingPermission?.has_create || permission.has_create,
        has_read: existingPermission?.has_read || permission.has_read,
        has_update: existingPermission?.has_update || permission.has_update,
        has_delete: existingPermission?.has_delete || permission.has_delete,
      });

      const rows = fieldRowsByAcoId.get(acoId) ?? [];
      rows.push({
        has_create: permission.has_create,
        has_read: permission.has_read,
        has_update: permission.has_update,
        field_paths: parseAllowedPaths(permission.field_paths),
      });
      fieldRowsByAcoId.set(acoId, rows);
    });

    // Helper function to get all ancestors of an ACO (including itself)
    const getAllAncestors = (aco: PyloAco): PyloAco[] => {
      const ancestors = [aco];
      let current = aco;
      while (current.parent?.data?.id) {
        const parent = acosMap.get(current.parent.data.id);
        if (!parent) break;
        ancestors.unshift(parent);
        current = parent;
      }
      return ancestors;
    };

    const permissionsForEntityIds = new Map<string, EntityPermissions>();
    const fieldPermissionsForEntityIds = new Map<string, FieldPermissionRow[]>();

    // Process each ACO that has an entity
    for (const aco of acos.data ?? []) {
      if (!aco.entity?.data?.id) continue;

      const entityId = aco.entity.data.id;

      // Get all ancestors of this ACO (including itself)
      const ancestors = getAllAncestors(aco);

      const foundPermissions: EntityPermissions[] = [];
      const foundFieldRows: FieldPermissionRow[] = [];

      // Check each ancestor for permissions
      for (const ancestor of ancestors) {
        const permission = permissionsByAcoId.get(ancestor.id);
        if (permission) foundPermissions.push(permission);

        const fieldRows = fieldRowsByAcoId.get(ancestor.id);
        if (fieldRows) foundFieldRows.push(...fieldRows);
      }

      const mergedPermission: EntityPermissions = {
        has_create: foundPermissions.some((p) => p.has_create),
        has_read: foundPermissions.some((p) => p.has_read),
        has_update: foundPermissions.some((p) => p.has_update),
        has_delete: foundPermissions.some((p) => p.has_delete),
      };

      // An entity can be referenced by several ACOs (permission-bundle folders); access via any
      // of its chains grants, mirroring the backend union — so merge instead of overwriting.
      const existing = permissionsForEntityIds.get(entityId);
      permissionsForEntityIds.set(entityId, {
        has_create: existing?.has_create || mergedPermission.has_create,
        has_read: existing?.has_read || mergedPermission.has_read,
        has_update: existing?.has_update || mergedPermission.has_update,
        has_delete: existing?.has_delete || mergedPermission.has_delete,
      });
      fieldPermissionsForEntityIds.set(entityId, [
        ...(fieldPermissionsForEntityIds.get(entityId) ?? []),
        ...foundFieldRows,
      ]);
    }

    return { permissionsForEntityIds, permissionsByAcoId, fieldPermissionsForEntityIds };
  }, [permissions, acos]);

  const hasRootPrivileges = useMemo(() => {
    const rootAcoPermission = entityPermissionsMap.permissionsByAcoId.get(
      CONSTANTS.ENTITY_UIDS.PYLO_ROOT_ACO,
    );
    return !!(
      rootAcoPermission?.has_create &&
      rootAcoPermission?.has_update &&
      rootAcoPermission?.has_delete
    );
  }, [entityPermissionsMap.permissionsByAcoId]);

  useEffect(() => {
    setIsLoading(isLoadingCombined);
  }, [isLoadingCombined, setIsLoading]);

  useEffect(() => {
    // Mark permissions ready once both source queries have settled — success OR
    // error. Using isFetched (rather than data !== undefined) means a failed
    // permissions fetch still releases the UI instead of leaving consumers
    // (e.g. the sidebar skeleton) stuck forever.
    if (isFetchedPermissions && isFetchedAcos) {
      setEntityPermissionsMap(entityPermissionsMap.permissionsForEntityIds);
      setEntityFieldPermissionsMap(entityPermissionsMap.fieldPermissionsForEntityIds);
      setHasRootPrivileges(hasRootPrivileges);
      setHasLoaded(true);
    }
  }, [
    isFetchedPermissions,
    isFetchedAcos,
    entityPermissionsMap,
    setEntityPermissionsMap,
    setEntityFieldPermissionsMap,
    setHasRootPrivileges,
    hasRootPrivileges,
    setHasLoaded,
  ]);

  return entityPermissionsMap.permissionsForEntityIds;
};
